/**
 * Atomically merge the Synkro guardrails MCP server entry into ~/.claude.json.
 *
 * CC's MCP config lives in ~/.claude.json (NOT ~/.claude/settings.json — those
 * are different files). It accepts an HTTP-transport server with a static
 * Authorization header set at config-write time. We register one entry,
 * `synkro-guardrails`, marked with `__synkro_managed__: true` so we can safely
 * remove it on `synkro disconnect` without touching the user's other servers.
 *
 * Mirrors the pattern in ccHookConfig.ts — read-modify-write atomically via
 * tmpfile + rename; preserve any other top-level keys; never replace the file.
 */
import { existsSync, readFileSync, writeFileSync, renameSync, mkdirSync } from 'node:fs';
import { homedir } from 'node:os';
import { dirname, join } from 'node:path';
import { spawnSync } from 'node:child_process';
import { randomBytes } from 'node:crypto';

const SYNKRO_MARKER = '__synkro_managed__';
const SYNKRO_SERVER_NAME = 'synkro-guardrails';
const CC_CONFIG_PATH = join(homedir(), '.claude.json');

interface ClaudeJson {
  mcpServers?: Record<string, McpServerEntry>;
  [k: string]: unknown;
}

interface McpServerEntry {
  type?: 'http' | 'stdio' | 'sse';
  url?: string;
  command?: string;
  args?: string[];
  headers?: Record<string, string>;
  env?: Record<string, string>;
  [SYNKRO_MARKER]?: boolean;
  [k: string]: unknown;
}

function readClaudeJson(): ClaudeJson {
  if (!existsSync(CC_CONFIG_PATH)) return {};
  try {
    const raw = readFileSync(CC_CONFIG_PATH, 'utf-8');
    return JSON.parse(raw) as ClaudeJson;
  } catch (err) {
    throw new Error(`Failed to parse ${CC_CONFIG_PATH}: ${(err as Error).message}`);
  }
}

function writeClaudeJsonAtomic(config: ClaudeJson): void {
  mkdirSync(dirname(CC_CONFIG_PATH), { recursive: true });
  const tmpPath = `${CC_CONFIG_PATH}.synkro.tmp`;
  writeFileSync(tmpPath, JSON.stringify(config, null, 2) + '\n', 'utf-8');
  renameSync(tmpPath, CC_CONFIG_PATH);
}

export interface InstallMcpOptions {
  gatewayUrl: string;     // e.g. http://localhost:8788
  // Long-lived (1y) Synkro-signed JWT scoped to mcp:guardrails. Minted by
  // POST /api/v1/cli/mcp-token during install. We deliberately do NOT write
  // the WorkOS access token here anymore — that one expires in 5 min and
  // silently breaks the CC MCP connection.
  bearerToken: string;
  local?: boolean;
}

/**
 * Register the Synkro guardrails MCP server in ~/.claude.json.
 * Idempotent — replaces any prior Synkro-managed entry with the new one.
 */
export function installMcpConfig(opts: InstallMcpOptions): { path: string; url: string } {
  const config = readClaudeJson();
  config.mcpServers = config.mcpServers ?? {};

  // Remove any prior Synkro-managed entry (so re-running install picks up
  // a refreshed JWT). Leave non-Synkro entries alone.
  for (const [name, entry] of Object.entries(config.mcpServers)) {
    if (entry?.[SYNKRO_MARKER] === true) delete config.mcpServers[name];
  }

  if (opts.local) {
    const proxyScript = join(homedir(), '.synkro', 'hooks', 'mcp-stdio-proxy.ts');
    config.mcpServers[SYNKRO_SERVER_NAME] = {
      type: 'stdio',
      command: 'bun',
      args: ['run', proxyScript],
      [SYNKRO_MARKER]: true,
    };
    writeClaudeJsonAtomic(config);
    return { path: CC_CONFIG_PATH, url: `stdio://${proxyScript}` };
  }

  const url = `${opts.gatewayUrl.replace(/\/$/, '')}/api/v1/mcp/guardrails`;
  config.mcpServers[SYNKRO_SERVER_NAME] = {
    type: 'http',
    url,
    headers: { Authorization: `Bearer ${opts.bearerToken}` },
    [SYNKRO_MARKER]: true,
  };

  writeClaudeJsonAtomic(config);
  return { path: CC_CONFIG_PATH, url };
}

/**
 * Remove all Synkro-managed MCP server entries from ~/.claude.json.
 * Returns true if anything was removed.
 */
export function uninstallMcpConfig(): boolean {
  if (!existsSync(CC_CONFIG_PATH)) return false;
  const config = readClaudeJson();
  if (!config.mcpServers || Object.keys(config.mcpServers).length === 0) return false;

  let removed = false;
  for (const [name, entry] of Object.entries(config.mcpServers)) {
    if (entry?.[SYNKRO_MARKER] === true) {
      delete config.mcpServers[name];
      removed = true;
    }
  }
  if (!removed) return false;

  // If the mcpServers object is now empty, drop it to keep the file tidy.
  if (Object.keys(config.mcpServers).length === 0) delete config.mcpServers;

  writeClaudeJsonAtomic(config);
  return true;
}

/**
 * Inspect whether the Synkro MCP server entry is currently registered.
 */
export function inspectMcpConfig(): {
  installed: boolean;
  configPath: string;
  url?: string;
} {
  if (!existsSync(CC_CONFIG_PATH)) {
    return { installed: false, configPath: CC_CONFIG_PATH };
  }
  const config = readClaudeJson();
  const entry = config.mcpServers?.[SYNKRO_SERVER_NAME];
  if (!entry || entry[SYNKRO_MARKER] !== true) {
    return { installed: false, configPath: CC_CONFIG_PATH };
  }
  return { installed: true, configPath: CC_CONFIG_PATH, url: entry.url };
}

// ─── Cursor MCP Config ───

const CURSOR_MCP_PATH = join(homedir(), '.cursor', 'mcp.json');

interface CursorMcpJson {
  mcpServers?: Record<string, McpServerEntry>;
  [k: string]: unknown;
}

function readCursorMcpJson(): CursorMcpJson {
  if (!existsSync(CURSOR_MCP_PATH)) return {};
  try {
    const raw = readFileSync(CURSOR_MCP_PATH, 'utf-8');
    return JSON.parse(raw) as CursorMcpJson;
  } catch (err) {
    throw new Error(`Failed to parse ${CURSOR_MCP_PATH}: ${(err as Error).message}`);
  }
}

function writeCursorMcpJsonAtomic(config: CursorMcpJson): void {
  mkdirSync(dirname(CURSOR_MCP_PATH), { recursive: true });
  const tmpPath = `${CURSOR_MCP_PATH}.synkro.tmp`;
  writeFileSync(tmpPath, JSON.stringify(config, null, 2) + '\n', 'utf-8');
  renameSync(tmpPath, CURSOR_MCP_PATH);
}

/**
 * Register the Synkro guardrails MCP server in ~/.cursor/mcp.json.
 * Idempotent — replaces any prior Synkro-managed entry with the new one.
 */
export function installCursorMcpConfig(opts: InstallMcpOptions): { path: string; url: string } {
  const config = readCursorMcpJson();
  config.mcpServers = config.mcpServers ?? {};

  for (const [name, entry] of Object.entries(config.mcpServers)) {
    if (entry?.[SYNKRO_MARKER] === true) delete config.mcpServers[name];
  }

  if (opts.local) {
    // Honors SYNKRO_MCP_PORT so the containerised deployment can register
    // its host-mapped port (e.g. 18931) instead of the bare-host default.
    const port = process.env.SYNKRO_MCP_PORT || '18931';
    const url = `http://127.0.0.1:${port}/`;
    const jwtPath = join(homedir(), '.synkro', '.mcp-jwt');
    let jwt = '';
    try { jwt = readFileSync(jwtPath, 'utf-8').trim(); } catch {}
    config.mcpServers[SYNKRO_SERVER_NAME] = {
      url,
      ...(jwt ? { headers: { Authorization: `Bearer ${jwt}` } } : {}),
      [SYNKRO_MARKER]: true,
    };
    writeCursorMcpJsonAtomic(config);
    return { path: CURSOR_MCP_PATH, url };
  }

  const url = `${opts.gatewayUrl.replace(/\/$/, '')}/api/v1/mcp/guardrails`;
  config.mcpServers[SYNKRO_SERVER_NAME] = {
    url,
    headers: { Authorization: `Bearer ${opts.bearerToken}` },
    [SYNKRO_MARKER]: true,
  };

  writeCursorMcpJsonAtomic(config);
  return { path: CURSOR_MCP_PATH, url };
}

/**
 * Remove all Synkro-managed MCP server entries from ~/.cursor/mcp.json.
 */
export function uninstallCursorMcpConfig(): boolean {
  if (!existsSync(CURSOR_MCP_PATH)) return false;
  const config = readCursorMcpJson();
  if (!config.mcpServers || Object.keys(config.mcpServers).length === 0) return false;

  let removed = false;
  for (const [name, entry] of Object.entries(config.mcpServers)) {
    if (entry?.[SYNKRO_MARKER] === true) {
      delete config.mcpServers[name];
      removed = true;
    }
  }
  if (!removed) return false;

  if (Object.keys(config.mcpServers).length === 0) delete config.mcpServers;

  writeCursorMcpJsonAtomic(config);
  return true;
}

// ─── Claude Desktop MCP Config ───
//
// Claude Desktop reads MCP servers from claude_desktop_config.json and (unlike
// Cursor / Claude Code) supports ONLY stdio command servers there — no `url`
// transport. So we register the SAME stdio proxy Claude Code's local mode uses
// (~/.synkro/hooks/mcp-stdio-proxy.ts), which bridges stdio ⇄ the local
// synkro-server on :18931 and reads the JWT from ~/.synkro/.mcp-jwt itself (no
// secret in the config). This lets the user manage Synkro standards
// (rules/tasks/findings) from Claude Desktop, not just Claude Code.

const CLAUDE_DESKTOP_CONFIG_PATH = join(
  homedir(), 'Library', 'Application Support', 'Claude', 'claude_desktop_config.json',
);
const MCP_STDIO_PROXY_PATH = join(homedir(), '.synkro', 'hooks', 'mcp-stdio-proxy.ts');

/**
 * Absolute path to `bun`. Claude Desktop is a GUI app and does NOT inherit the
 * login-shell PATH, so a bare "bun" command fails to spawn. Resolve it to an
 * absolute path at config-write time, with sensible fallbacks.
 */
function resolveBunBin(): string {
  const r = spawnSync('which', ['bun'], { encoding: 'utf-8', timeout: 5_000 });
  const resolved = (r.stdout || '').split('\n')[0].trim();
  if (resolved) return resolved;
  for (const p of ['/opt/homebrew/bin/bun', '/usr/local/bin/bun', join(homedir(), '.bun', 'bin', 'bun')]) {
    if (existsSync(p)) return p;
  }
  return 'bun';
}

function readDesktopJson(): ClaudeJson {
  if (!existsSync(CLAUDE_DESKTOP_CONFIG_PATH)) return {};
  try {
    return JSON.parse(readFileSync(CLAUDE_DESKTOP_CONFIG_PATH, 'utf-8')) as ClaudeJson;
  } catch (err) {
    throw new Error(`Failed to parse ${CLAUDE_DESKTOP_CONFIG_PATH}: ${(err as Error).message}`);
  }
}

function writeDesktopJsonAtomic(config: ClaudeJson): void {
  mkdirSync(dirname(CLAUDE_DESKTOP_CONFIG_PATH), { recursive: true });
  // Unique per-write tmp name (pid + random) so concurrent writers can't race
  // on a shared tmpfile between write and rename (CWE-362). rename(2) onto the
  // final path stays atomic regardless of how many writers there are.
  const tmpPath = `${CLAUDE_DESKTOP_CONFIG_PATH}.synkro.${process.pid}.${randomBytes(6).toString('hex')}.tmp`;
  writeFileSync(tmpPath, JSON.stringify(config, null, 2) + '\n', 'utf-8');
  renameSync(tmpPath, CLAUDE_DESKTOP_CONFIG_PATH);
}

export interface InstallDesktopMcpResult {
  ok: boolean;
  path: string;
  proxy: string;
  reason?: string;
}

/**
 * Register the Synkro guardrails MCP server in Claude Desktop's config.
 * Idempotent. Returns ok:false (without writing) if the stdio proxy isn't on
 * disk yet — that means `synkro install` hasn't run, so there's nothing to
 * bridge to.
 */
export function installClaudeDesktopMcpConfig(): InstallDesktopMcpResult {
  if (!existsSync(MCP_STDIO_PROXY_PATH)) {
    return {
      ok: false,
      path: CLAUDE_DESKTOP_CONFIG_PATH,
      proxy: MCP_STDIO_PROXY_PATH,
      reason: 'stdio proxy not found — run `synkro install` first',
    };
  }
  const config = readDesktopJson();
  config.mcpServers = config.mcpServers ?? {};

  // Replace any prior Synkro-managed entry; leave the user's other servers alone.
  for (const [name, entry] of Object.entries(config.mcpServers)) {
    if (entry?.[SYNKRO_MARKER] === true) delete config.mcpServers[name];
  }

  config.mcpServers[SYNKRO_SERVER_NAME] = {
    command: resolveBunBin(),
    args: ['run', MCP_STDIO_PROXY_PATH],
    env: { SYNKRO_MCP_PORT: process.env.SYNKRO_MCP_PORT || '18931' },
    [SYNKRO_MARKER]: true,
  };

  writeDesktopJsonAtomic(config);
  return { ok: true, path: CLAUDE_DESKTOP_CONFIG_PATH, proxy: MCP_STDIO_PROXY_PATH };
}

/** Remove all Synkro-managed MCP entries from Claude Desktop's config. */
export function uninstallClaudeDesktopMcpConfig(): boolean {
  if (!existsSync(CLAUDE_DESKTOP_CONFIG_PATH)) return false;
  const config = readDesktopJson();
  if (!config.mcpServers || Object.keys(config.mcpServers).length === 0) return false;

  let removed = false;
  for (const [name, entry] of Object.entries(config.mcpServers)) {
    if (entry?.[SYNKRO_MARKER] === true) {
      delete config.mcpServers[name];
      removed = true;
    }
  }
  if (!removed) return false;

  if (Object.keys(config.mcpServers).length === 0) delete config.mcpServers;
  writeDesktopJsonAtomic(config);
  return true;
}

/** Whether the Synkro MCP entry is registered in Claude Desktop's config. */
export function inspectClaudeDesktopMcpConfig(): { installed: boolean; configPath: string } {
  if (!existsSync(CLAUDE_DESKTOP_CONFIG_PATH)) {
    return { installed: false, configPath: CLAUDE_DESKTOP_CONFIG_PATH };
  }
  try {
    const entry = readDesktopJson().mcpServers?.[SYNKRO_SERVER_NAME];
    return { installed: entry?.[SYNKRO_MARKER] === true, configPath: CLAUDE_DESKTOP_CONFIG_PATH };
  } catch {
    return { installed: false, configPath: CLAUDE_DESKTOP_CONFIG_PATH };
  }
}
